import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import {
  EditorState,
  convertToRaw,
  ContentState,
  Modifier,
  SelectionState,
} from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

interface ControlledEditorProps {
  initialValue: string;
  onChange: () => unknown;
}

const ControlledEditor: React.FC<ControlledEditorProps> = forwardRef(
  ({ onChange, initialValue }, ref) => {
    const editorRef = useRef(null);
    const [editorState, setEditorState] = useState(EditorState.createEmpty());
    const [currentSelection, setCurrentSelection] = useState(null);

    useImperativeHandle(ref, () => ({
      updateBody(text: string) {
        const contentBlock = htmlToDraft(text);
        if (text && contentBlock) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks,
          );
          setEditorState(EditorState.createWithContent(contentState));
        }
      },
      pushVar(text) {
        const currentEditorState = editorState;
        if (!currentEditorState) return;

        let selection = currentEditorState.getSelection();
        const contentState = currentEditorState.getCurrentContent();

        // If no interaction, set the selection to the end of the content
        if (
          selection.isCollapsed() &&
          contentState.getPlainText().trim() === ''
        ) {
          const endKey = contentState.getLastBlock().getKey();
          const endOffset = contentState.getLastBlock().getText().length;
          selection = SelectionState.createEmpty(endKey).merge({
            anchorOffset: endOffset,
            focusOffset: endOffset,
          });
        }

        const updatedContentState = Modifier.insertText(
          contentState,
          selection,
          text,
        );

        const newEditorState = EditorState.push(
          currentEditorState,
          updatedContentState,
          'insert-characters',
        );

        setEditorState(EditorState.forceSelection(newEditorState, selection));
      },
    }));

    const handleCursorEvent = () => {
      const selectionState = editorState.getSelection();
      setCurrentSelection(selectionState);
    };

    useEffect(() => {
      if (onChange && editorState && editorState.getCurrentContent) {
        const text = draftToHtml(convertToRaw(editorState.getCurrentContent()));
        onChange(text);
      }
    }, [editorState]);

    useEffect(() => {
      if (initialValue) {
        const contentBlock = htmlToDraft(initialValue);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks,
          );
          const initialEditorState = EditorState.createWithContent(
            contentState,
          );
          setEditorState(initialEditorState);
        }
      }
    }, []);

    const onEditorStateChange = newEditorState => {
      setEditorState(newEditorState);
    };

    return (
      <Editor
        ref={editorRef}
        editorState={editorState}
        wrapperClassName="demo-wrapper"
        editorClassName="demo-editor"
        onEditorStateChange={onEditorStateChange}
        onBlur={handleCursorEvent}
        onFocus={handleCursorEvent}
        toolbar={{
          options: [
            'colorPicker',
            'link',
            'embedded',
            'image',
            'inline',
            'blockType',
            'fontSize',
            'fontFamily',
            'list',
            'textAlign',
          ],
          inline: {
            options: ['bold', 'italic', 'underline', 'strikethrough'],
          },
          blockType: {
            options: [
              'Normal',
              'H1',
              'H2',
              'H3',
              'H4',
              'H5',
              'H6',
              'Blockquote',
            ],
          },
          colorPicker: {
            popupClassName: 'color-popup',
            portalTarget: document.body,
          },
        }}
      />
    );
  },
);

export default ControlledEditor;
